import type { CianMFUIElement } from '@cian/mf-registry/browser/internal/cianMFUI';
import { useCallback, useMemo, useRef } from 'react';
import { EStatus, useMicrofrontend } from 'shared/hooks';
import { fetchSaveSearchMicrofrontendManifest } from 'shared/services/fetchSaveSearchMicrofrontendManifest';
import { useSaveSearchMicrofrontendApi } from './useSearchMicrofrontendApi';

export const useSaveSearchMicrofrontend = () => {
  const microfrontendRef = useRef<CianMFUIElement | null>(null);

  const {
    manifest,
    fetchManifest,
    status: microfrontendStatus,
  } = useMicrofrontend({
    fetchFunction: fetchSaveSearchMicrofrontendManifest,
  });

  const { isMicrofrontendApiReady, setMicrofrontendApiReady, openSaveSearchModal } =
    useSaveSearchMicrofrontendApi(microfrontendRef);

  const prepareSaveSearchMicrofrontend = useCallback(async () => {
    await fetchManifest();

    // Ждём пока микрофронт замаунтится и будет готов к тому чтобы мы постучались к инстансу.
    // Сейчас это необходимо для доступа к локальному API.
    await new Promise(resolve => setTimeout(resolve, 0));

    setMicrofrontendApiReady();
  }, [fetchManifest, setMicrofrontendApiReady]);

  return useMemo(
    () => ({
      manifest,
      microfrontendRef,
      /** Считаем, что микрофронт еще загружается, если его АПИ не готово к работе */
      isLoading:
        microfrontendStatus === EStatus.Loading ||
        (microfrontendStatus === EStatus.Success && !isMicrofrontendApiReady),
      isError: microfrontendStatus === EStatus.Error,
      prepareSaveSearchMicrofrontend,
      setMicrofrontendApiReady,
      openSaveSearchModal,
    }),
    [
      isMicrofrontendApiReady,
      manifest,
      microfrontendStatus,
      openSaveSearchModal,
      prepareSaveSearchMicrofrontend,
      setMicrofrontendApiReady,
    ],
  );
};
